import type { ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useAdminLogout,
  useAdminMe,
  useExitImpersonation,
} from '../hooks/useAdminMe.js'
import { ThemeToggle } from './ThemeToggle.js'

/**
 * Top-level admin nav. Tenants is the home; the rest are the cross-cutting
 * superadmin surfaces added in slice 3c. Account lives in the header user
 * area rather than the sidebar (per the slice 3c §4 note).
 */
const NAV_ITEMS: Array<{ to: string; label: string }> = [
  { to: '/admin/tenants', label: 'Tenants' },
  { to: '/admin/invoices', label: 'Invoices' },
  { to: '/admin/analytics', label: 'Analytics' },
  { to: '/admin/plan-tiers', label: 'Plan Tiers' },
  { to: '/admin/audit-log', label: 'Audit Log' },
  { to: '/admin/admins', label: 'Admins' },
  { to: '/admin/system', label: 'System' },
]

/** Header + sidebar chrome shared by every authenticated admin page. */
export function AdminLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { data: me } = useAdminMe()
  const logout = useAdminLogout()
  const exitImpersonation = useExitImpersonation()

  function handleSignOut() {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/admin/login', { replace: true }),
      onError: () => toast.error('Sign-out failed — try again.'),
    })
  }

  function handleExitImpersonation() {
    exitImpersonation.mutate(undefined, {
      onSuccess: () => toast.success('Exited impersonation.'),
      onError: () => toast.error('Could not exit impersonation — try again.'),
    })
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <Link to="/admin/tenants" className="admin-header-brand">
          Naya<span> · admin</span>
        </Link>
        <nav className="admin-nav" aria-label="Admin sections">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `admin-nav-link${isActive ? ' admin-nav-link-active' : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="admin-header-right">
          <ThemeToggle />
          {me?.admin && (
            <Link
              to="/admin/account"
              className="admin-header-email"
              title="Account settings"
            >
              {me.admin.email}
            </Link>
          )}
          <button
            type="button"
            className="admin-btn admin-btn-sm"
            onClick={handleSignOut}
            disabled={logout.isPending}
          >
            Sign out
          </button>
        </div>
      </header>
      {me?.impersonating && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            background: '#C8553D',
            color: '#fff',
            padding: '10px 16px',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          <span>
            You're impersonating a tenant owner (open in another tab). Admin
            pages are paused until you exit.
          </span>
          <button
            type="button"
            className="admin-btn admin-btn-sm"
            onClick={handleExitImpersonation}
            disabled={exitImpersonation.isPending}
          >
            {exitImpersonation.isPending ? 'Exiting…' : 'Exit impersonation'}
          </button>
        </div>
      )}
      <main className="admin-main">{children}</main>
    </div>
  )
}
